import { useState } from "react";
import dayjs from "dayjs";
import { Plus } from "lucide-react";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { NavBar } from "../../UI/Layout/NavBar.tsx";
import { AdminMainContainer } from "../../UI/Admin/AdminMainContainer.tsx";
import { FullPageLoading } from "../../UI/FullPageLoading.tsx";
import { ErrorState } from "../../UI/ErrorState.tsx";
import { CopyableText } from "../../UI/CopyableText.tsx";
import { DebouncedSearchInput } from "../../UI/DebouncedSearchInput.tsx";
import { Pagination } from "../../UI/Pagination.tsx";
import { TableColumnHeader } from "../../UI/Table/TableColumnHeader.tsx";
import { DateRangeFilter } from "../../UI/Table/Filters/DateRangeFilter.tsx";
import {
  CheckboxListFilter,
  CheckboxListOption,
} from "../../UI/Table/Filters/CheckboxListFilter.tsx";
import {
  hasActiveListFilters,
  SortDirection,
} from "../../UI/Table/listUrlParams.ts";
import { useAllWithdrawalRequests } from "../../SpeakingClassService/Withdrawal/useAllWithdrawalRequests.ts";
import {
  WithdrawalRequestDTO,
  WithdrawalRequestSource,
  WithdrawalRequestStatus,
} from "../../SpeakingClassService/Withdrawal/WithdrawalRequestDTO.ts";
import {
  pickWithdrawalRequestsFilters,
  WithdrawalRequestSortField,
  WithdrawalRequestsFilters,
} from "../../SpeakingClassService/Withdrawal/WithdrawalRequestsListQuery.ts";
import {
  WITHDRAWAL_SOURCE_LABELS,
  WITHDRAWAL_STATUS_COLORS,
  WITHDRAWAL_STATUS_LABELS,
} from "./withdrawalLabels.ts";
import { CreateWithdrawalRequestModal } from "./CreateWithdrawalRequestModal.tsx";
import { WithdrawalRequestDetailsModal } from "./WithdrawalRequestDetailsModal.tsx";
import { extractGatewayOrderId } from "./useWithdrawalOrderSummary.ts";
import { extractDealId } from "./extractDealId.ts";
import { useWithdrawalRequestsListUrlState } from "./useWithdrawalRequestsListUrlState.ts";

export const AllWithdrawalRequestsScreenPath = "/admin/withdrawal-requests";

const DATE_SORT_LABELS = { asc: "Dal meno recente", desc: "Dal più recente" };
const CUSTOMER_SORT_LABELS = { asc: "A → Z", desc: "Z → A" };

function WithdrawalStatusBadge({
  status,
}: {
  status: WithdrawalRequestStatus;
}) {
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${WITHDRAWAL_STATUS_COLORS[status]}`}
    >
      {WITHDRAWAL_STATUS_LABELS[status]}
    </span>
  );
}

const STATUS_OPTIONS: CheckboxListOption<WithdrawalRequestStatus>[] =
  Object.values(WithdrawalRequestStatus).map((status) => ({
    value: status,
    label: <WithdrawalStatusBadge status={status} />,
  }));

const SOURCE_OPTIONS: CheckboxListOption<WithdrawalRequestSource>[] =
  Object.values(WithdrawalRequestSource).map((source) => ({
    value: source,
    label: WITHDRAWAL_SOURCE_LABELS[source],
  }));

export function AllWithdrawalRequestsScreen() {
  const [selectedRequest, setSelectedRequest] =
    useState<WithdrawalRequestDTO | null>(null);
  const [creating, setCreating] = useState(false);

  const { query, setSearch, setSort, setFilters, toPage } =
    useWithdrawalRequestsListUrlState();
  const { requests, total, isLoading, error, refetch } =
    useAllWithdrawalRequests(query);

  const filters = pickWithdrawalRequestsFilters(query);
  const hasFilters = hasActiveListFilters(filters);
  const totalPages = Math.max(1, Math.ceil(total / query.pageSize));
  const updateFilters = (changes: WithdrawalRequestsFilters) =>
    setFilters({ ...filters, ...changes });
  const sortProps = (field: WithdrawalRequestSortField) => ({
    sortDirection: query.sortBy === field ? query.sortDirection : undefined,
    onSort: (direction: SortDirection) => setSort(field, direction),
  });

  if (isLoading) return <FullPageLoading />;

  return (
    <Layout>
      <AdminMainContainer>
        <NavBar showAdminSideBarHandle />
        <div className="flex-1 p-4 flex flex-col gap-4 max-w-full">
          <div className="flex items-center gap-3">
            <h1 className="font-bold text-3xl flex-1">Richieste di recesso</h1>
            <button
              onClick={() => setCreating(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 shrink-0"
            >
              <Plus className="w-4 h-4" />
              Nuova richiesta
            </button>
          </div>

          <DebouncedSearchInput
            placeholder="Cerca per nome, email, ID ordine, deal HubSpot o closer"
            value={query.search ?? ""}
            onSearch={(input) => setSearch(extractDealId(input))}
          />

          {error ? (
            <ErrorState />
          ) : (
            <>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span>{total} richieste trovate</span>
                {hasFilters && (
                  <button
                    type="button"
                    onClick={() => setFilters({})}
                    className="text-blue-600 hover:underline"
                  >
                    Rimuovi tutti i filtri
                  </button>
                )}
              </div>

              <div className="overflow-x-auto max-w-full">
                <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th className="px-6 py-4">
                        <TableColumnHeader
                          label="Data richiesta"
                          {...sortProps("requestedAt")}
                          sortLabels={DATE_SORT_LABELS}
                          isFiltered={
                            !!(filters.requestedFrom || filters.requestedTo)
                          }
                          renderFilter={(close) => (
                            <DateRangeFilter
                              value={{
                                from: filters.requestedFrom,
                                to: filters.requestedTo,
                              }}
                              onApply={({ from, to }) =>
                                updateFilters({
                                  requestedFrom: from,
                                  requestedTo: to,
                                })
                              }
                              close={close}
                            />
                          )}
                        />
                      </th>
                      <th className="px-6 py-4">
                        <TableColumnHeader
                          label="Cliente"
                          {...sortProps("customer")}
                          sortLabels={CUSTOMER_SORT_LABELS}
                        />
                      </th>
                      <th className="px-6 py-4">
                        <TableColumnHeader
                          label="Data acquisto"
                          {...sortProps("purchasedAt")}
                          sortLabels={DATE_SORT_LABELS}
                          isFiltered={
                            !!(filters.purchasedFrom || filters.purchasedTo)
                          }
                          renderFilter={(close) => (
                            <DateRangeFilter
                              value={{
                                from: filters.purchasedFrom,
                                to: filters.purchasedTo,
                              }}
                              onApply={({ from, to }) =>
                                updateFilters({
                                  purchasedFrom: from,
                                  purchasedTo: to,
                                })
                              }
                              close={close}
                            />
                          )}
                        />
                      </th>
                      <th className="px-6 py-4">
                        <TableColumnHeader
                          label="Origine"
                          isFiltered={!!filters.sources?.length}
                          renderFilter={(close) => (
                            <CheckboxListFilter
                              options={SOURCE_OPTIONS}
                              value={filters.sources ?? []}
                              onApply={(sources) => updateFilters({ sources })}
                              close={close}
                            />
                          )}
                        />
                      </th>
                      <th className="px-6 py-4">
                        <TableColumnHeader
                          label="Stato"
                          isFiltered={!!filters.statuses?.length}
                          renderFilter={(close) => (
                            <CheckboxListFilter
                              options={STATUS_OPTIONS}
                              value={filters.statuses ?? []}
                              onApply={(statuses) =>
                                updateFilters({ statuses })
                              }
                              close={close}
                            />
                          )}
                        />
                      </th>
                      <th className="px-6 py-4">Ordine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.length === 0 && (
                      <tr className="bg-white">
                        <td
                          colSpan={6}
                          className="px-6 py-10 text-center text-gray-500"
                        >
                          Nessuna richiesta trovata.
                        </td>
                      </tr>
                    )}
                    {requests.map((request) => {
                      const gatewayOrderId = extractGatewayOrderId(
                        request.orderId,
                      );
                      return (
                        <tr
                          key={request.id}
                          className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600"
                          onClick={() => setSelectedRequest(request)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            {dayjs(request.requestedAtUtc).format("DD/MM/YYYY")}
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {request.userGivenName} {request.userFamilyName}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400 text-xs">
                              {request.userEmail}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {dayjs(request.purchasedAtUtc).format("DD/MM/YYYY")}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {WITHDRAWAL_SOURCE_LABELS[request.source]}
                          </td>
                          <td className="px-6 py-4">
                            <WithdrawalStatusBadge status={request.status} />
                          </td>
                          <td
                            className="px-6 py-4 max-w-[220px] truncate"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {gatewayOrderId ? (
                              <CopyableText
                                className="cursor-pointer hover:text-blue-600"
                                value={gatewayOrderId}
                                copyMessage="ID ordine copiato negli appunti!"
                              >
                                {gatewayOrderId}
                              </CopyableText>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={query.page}
                totalPages={totalPages}
                onChangePage={toPage}
              />
            </>
          )}
        </div>

        {selectedRequest && (
          <WithdrawalRequestDetailsModal
            request={selectedRequest}
            onClose={() => setSelectedRequest(null)}
            onUpdated={async () => {
              await refetch();
              setSelectedRequest(null);
            }}
          />
        )}

        {creating && (
          <CreateWithdrawalRequestModal
            onClose={() => setCreating(false)}
            onCreated={async () => {
              await refetch();
              setCreating(false);
            }}
          />
        )}
      </AdminMainContainer>
    </Layout>
  );
}
